import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { CoachingMilestoneView } from '@euler/model/coaching-progress';
import dayjs from 'dayjs';
import { Award, ChevronDown, ChevronUp, Lock } from 'lucide-react-native';
import { memo, useState } from 'react';
import { Pressable, View } from 'react-native';
import { aiColors } from '../../../ai-suite/theme';
import { kLockedMilestones, milestoneTitle } from '../progressCopy';

/** Earned milestones shown before the rest is folded away. */
const kFolded = 5;

/**
 * Earned milestones, the latest few until unfolded, then a few still to
 * earn with what earns them.
 */
export const MilestoneList = memo(
  ({ milestones }: { milestones: CoachingMilestoneView[] }) => {
    const { rpx } = useResponsiveContext();
    const earned = new Set(milestones.map(x => x.kind));
    const locked = kLockedMilestones
      .filter(x => !earned.has(x.kind))
      .slice(0, 3);
    const [unfolded, setUnfolded] = useState(false);
    const foldable = milestones.length > kFolded;
    const shown =
      foldable && !unfolded ? milestones.slice(0, kFolded) : milestones;
    const Chevron = unfolded ? ChevronUp : ChevronDown;
    return (
      <View style={{ gap: rpx(10) }}>
        {shown.map(x => (
          <View
            key={x.id}
            style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(10) }}
          >
            <Award size={rpx(18)} color={aiColors.amber} />
            <Typography.Text
              size={rpx(14)}
              color={aiColors.foreground}
              style={{ flex: 1 }}
            >
              {milestoneTitle(x)}
            </Typography.Text>
            <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
              {dayjs(x.earnedAt).format('M月D日')}
            </Typography.Text>
          </View>
        ))}
        {foldable ? (
          <Pressable
            accessibilityRole="button"
            onPress={() => setUnfolded(x => !x)}
            hitSlop={rpx(6)}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              alignSelf: 'center',
              gap: rpx(4),
            }}
          >
            <Typography.Text size={rpx(13)} color={aiColors.primary}>
              {unfolded ? '收起' : `展开全部 ${milestones.length} 个`}
            </Typography.Text>
            <Chevron size={rpx(14)} color={aiColors.primary} />
          </Pressable>
        ) : null}
        {locked.map(x => (
          <View
            key={x.kind}
            style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(10) }}
          >
            <Lock size={rpx(18)} color={aiColors.mutedForeground} />
            <Typography.Text size={rpx(14)} color={aiColors.mutedForeground}>
              {x.condition}
            </Typography.Text>
          </View>
        ))}
      </View>
    );
  },
);
